import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { PersonsRoute, VerifyAdminRoute } from '@/config/router/routes';
import { appRepository } from '@/data/appRepositoryImpl';
import { getL10n } from '@/l10n/useL10n';
import type { L10n } from '@/l10n/types';
import { VerifyAdminType } from '@/presentation/pages/verifyAdmin/verifyAdminType';
import { appStateStore } from '@/presentation/stores/appStateStore';
import { EasyLoading } from '@/presentation/widgets/loading/loadingOverlay';
import { useTextController, type TextController } from '@/presentation/widgets/PrimaryInputField';
import { SnackbarManager } from '@/presentation/widgets/snackbar/snackbarManager';
import { DiagnosticsLog } from '@/utils/diagnostics/diagnosticsLog';
import { AppException, stackTraceOf } from '@/utils/exceptions/appException';
import { AppFailure, FailureOp } from '@/utils/exceptions/appFailure';
import { ServerAddress } from '@/utils/serverAddress';
import { StorageHelper } from '@/utils/storageHelper';

/**
 * Вход планшета — как в APK: пока в браузере нет сессии, киоск спрашивает
 * PIN и проверяет его тем же запросом, что экран PIN в APK:
 * `POST {адрес}/{язык}/api/v1/check-pin/` с `{code}`. Верный PIN новый
 * бекенд подтверждает парой access- и refresh-токенов — их сохраняет сам
 * [appRepository.checkPincode], и при следующем входе код уже не
 * спрашивается: маршруты пускают по [StorageHelper.hasTabletSession].
 */

/** PIN — только цифры, как на клавиатуре PIN в APK. */
const PIN_PATTERN = /^\d+$/;

/** Всё, что не цифра: из поля PIN оно убирается сразу при вводе. */
const NON_DIGITS = /\D/g;

/** `check-pin`: неверный код. */
const HTTP_UNAUTHORIZED = 401;

/** `check-pin`: у планшета не задан PIN (`code_not_configured`). */
const HTTP_FORBIDDEN = 403;

/** Исчерпан лимит попыток. */
const HTTP_TOO_MANY_REQUESTS = 429;

export interface TabletLoginController {
  /**
   * Заголовок панели пароля: имя планшета из списка сервера, как в APK
   * (`Cic work test`), а пока его нет — адрес (`lp.hron.uz/cic_work_test`).
   * `null` — в адресе нет планшета, входить не во что.
   */
  readonly tabletTitle: string | null;
  readonly password: TextController;
  /** Ввод в поле PIN: остаются только цифры. */
  readonly handlePinChanged: (value: string) => void;
  readonly submit: () => Promise<void>;
  /**
   * Дверь администратора: PIN → экран адреса. Без неё киоск без сессии
   * заперт — старый бекенд без токенов, удалённый slug или другой сервер.
   */
  readonly openSettings: () => void;
}

/** Сохранённый адрес, если в нём есть slug планшета. */
function storedTabletAddress(): ServerAddress | null {
  const baseUrl = StorageHelper.instance.baseUrl;
  if (baseUrl === null) return null;
  const address = ServerAddress.parse(baseUrl);
  return address.hasPath ? address : null;
}

/**
 * Имя планшета из `tablets/public` — там же его берёт экран адреса APK.
 * `null` — списка нет (404 у сервера без него) или планшета в нём нет:
 * тогда заголовком остаётся адрес. Сбой пишется в журнал, а не в тост:
 * без имени вход работает так же.
 */
async function fetchTabletName(address: ServerAddress): Promise<string | null> {
  try {
    const tablets = await appRepository.fetchPublicTablets(address.root);
    return tablets.find((tablet) => tablet.slug === address.path)?.name ?? null;
  } catch (e) {
    DiagnosticsLog.instance.failure(AppFailure.fromException(FailureOp.tablets, e, stackTraceOf(e)));
    return null;
  }
}

/** Отказ `check-pin` человеческой фразой. */
function refusalMessage(error: unknown, l10n: L10n): string {
  const status = error instanceof AppException ? error.statusCode : null;
  switch (status) {
    case HTTP_UNAUTHORIZED:
      return l10n.tablet_pin_wrong;
    case HTTP_FORBIDDEN:
      return l10n.tablet_pin_not_configured;
    case HTTP_TOO_MANY_REQUESTS:
      return l10n.tablet_auth_rate_limited;
    default:
      // Сеть, 5xx, неизвестный планшет (404) — пустить без сессии нельзя.
      return l10n.tablet_login_unavailable;
  }
}

/** Сбой — в журнал целиком, посетителю — одна фраза. */
function showRefusal(error: unknown): void {
  DiagnosticsLog.instance.failure(AppFailure.fromException(FailureOp.pin, error, stackTraceOf(error)));
  SnackbarManager.instance.showError({ title: refusalMessage(error, getL10n()) });
}

/**
 * Хеш принятого кода — как в APK после верного PIN: без сети по нему можно
 * выйти из киоска. Хеш считает `crypto.subtle`, которого нет вне
 * защищённого контекста; вход от этого не отменяется, сбой — в журнал.
 */
async function rememberPin(pin: number): Promise<void> {
  try {
    await StorageHelper.instance.savePinHash(pin);
  } catch (e) {
    DiagnosticsLog.instance.event(`хеш PIN не сохранён: ${String(e)}`, { isError: true });
  }
}

/** Выдал ли сервер сессию: старый бекенд принимает PIN без токенов. */
function sessionIssued(): boolean {
  const storage = StorageHelper.instance;
  const baseUrl = storage.baseUrl;
  return baseUrl !== null && storage.hasTabletSession(baseUrl);
}

export function useTabletLoginController(): TabletLoginController {
  const navigate = useNavigate();
  const [address] = useState(storedTabletAddress);
  const [tabletName, setTabletName] = useState<string | null>(null);
  const password = useTextController();

  /** Второе нажатие, пока ждём сервер, запрос не дублирует. */
  const submittingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (address === null) return;
    void fetchTabletName(address).then((name) => {
      if (mountedRef.current) setTabletName(name);
    });
  }, [address]);

  /** `check-pin`; `false` — отказ, о нём уже сказано. */
  const checkPin = async (pin: number): Promise<boolean> => {
    try {
      await appRepository.checkPincode(pin);
    } catch (error) {
      if (mountedRef.current) {
        password.clear();
        showRefusal(error);
      }
      return false;
    }
    if (sessionIssued()) return true;
    // PIN верный, но токенов нет — внутрь пустить значило бы тут же
    // вернуться сюда же: маршруты без сессии ведут на этот экран.
    if (mountedRef.current) {
      password.clear();
      SnackbarManager.instance.showError({ title: getL10n().tablet_login_unavailable });
    }
    DiagnosticsLog.instance.event('check-pin принял код, но не выдал токены', { isError: true });
    return false;
  };

  const submit = async (): Promise<void> => {
    const typed = password.value;
    if (address === null || !PIN_PATTERN.test(typed) || submittingRef.current) return;
    submittingRef.current = true;
    try {
      EasyLoading.dismiss();
      EasyLoading.show();
      const pin = Number(typed);
      if (!(await checkPin(pin)) || !mountedRef.current) return;
      await rememberPin(pin);
      // Со свежими токенами конфигурация придёт с ключами STT аватаров.
      // Её сбой вход не отменяет — тост о нём покажет само хранилище.
      await appStateStore.getState().checkAndSetBaseUrl();
      if (!mountedRef.current) return;
      PersonsRoute.go(navigate);
    } finally {
      submittingRef.current = false;
      EasyLoading.dismiss();
    }
  };

  return {
    tabletTitle: address === null ? null : (tabletName ?? `${address.host}/${address.path}`),
    password,
    handlePinChanged: (value: string) => password.setValue(value.replace(NON_DIGITS, '')),
    submit,
    openSettings: () => VerifyAdminRoute.go(navigate, VerifyAdminType.changeServer),
  };
}
